import { cliente } from "../http/cliente";
import type {
  BancoIn,
  BancoOut,
  BancoPatch,
  ContaCorrenteIn,
  ContaCorrenteOut,
  ContaCorrentePatch,
  EmpresaIn,
  EmpresaOut,
  EmpresaPatch,
  PerfilOut,
  UsuarioIn,
  UsuarioOut,
  UsuarioPatch,
} from "./tipos";

/*
 * Fetchers do modulo de Cadastros (FE-4 CP-A — ADR 0026).
 *
 * Camada fina sobre o `cliente` HTTP (CP-C): injeta Bearer, trata refresh/401/503
 * e normaliza erro em `ErroHttp`. Estes fetchers NAO tratam erro — propagam a
 * rejeicao do `cliente` para o TanStack Query (hooks em `./hooks`) decidir.
 *
 * `baseURL` do cliente ja e `/api`, entao os paths aqui sao relativos a ela.
 * PATCH usa `cliente.patch` (campos parciais). Contas sao nested por empresa:
 * o `empresaId` vai no PATH (o backend isola IDOR por path).
 */

/* ───────────────────────── Empresas ───────────────────────── */

/** `GET /api/empresas` — empresas no escopo do usuario. */
export async function buscarEmpresas(): Promise<EmpresaOut[]> {
  const resposta = await cliente.get<EmpresaOut[]>("/empresas");
  return resposta.data;
}

/** `GET /api/empresas/{id}`. */
export async function buscarEmpresa(id: string): Promise<EmpresaOut> {
  const resposta = await cliente.get<EmpresaOut>(`/empresas/${id}`);
  return resposta.data;
}

/** `POST /api/empresas` (201). */
export async function criarEmpresa(corpo: EmpresaIn): Promise<EmpresaOut> {
  const resposta = await cliente.post<EmpresaOut>("/empresas", corpo);
  return resposta.data;
}

/** `PATCH /api/empresas/{id}`. */
export async function atualizarEmpresa(
  id: string,
  corpo: EmpresaPatch,
): Promise<EmpresaOut> {
  const resposta = await cliente.patch<EmpresaOut>(`/empresas/${id}`, corpo);
  return resposta.data;
}

/* ───────────────────────── Bancos ───────────────────────── */

/** `GET /api/bancos` — catalogo global. */
export async function buscarBancos(): Promise<BancoOut[]> {
  const resposta = await cliente.get<BancoOut[]>("/bancos");
  return resposta.data;
}

/** `GET /api/bancos/{id}`. */
export async function buscarBanco(id: string): Promise<BancoOut> {
  const resposta = await cliente.get<BancoOut>(`/bancos/${id}`);
  return resposta.data;
}

/** `POST /api/bancos` (201). */
export async function criarBanco(corpo: BancoIn): Promise<BancoOut> {
  const resposta = await cliente.post<BancoOut>("/bancos", corpo);
  return resposta.data;
}

/** `PATCH /api/bancos/{id}`. */
export async function atualizarBanco(
  id: string,
  corpo: BancoPatch,
): Promise<BancoOut> {
  const resposta = await cliente.patch<BancoOut>(`/bancos/${id}`, corpo);
  return resposta.data;
}

/* ───────────────────────── Contas correntes ───────────────────────── */

/** `GET /api/empresas/{empresaId}/contas-correntes`. */
export async function buscarContas(
  empresaId: string,
): Promise<ContaCorrenteOut[]> {
  const resposta = await cliente.get<ContaCorrenteOut[]>(
    `/empresas/${empresaId}/contas-correntes`,
  );
  return resposta.data;
}

/** `GET /api/empresas/{empresaId}/contas-correntes/{contaId}`. */
export async function buscarConta(
  empresaId: string,
  contaId: string,
): Promise<ContaCorrenteOut> {
  const resposta = await cliente.get<ContaCorrenteOut>(
    `/empresas/${empresaId}/contas-correntes/${contaId}`,
  );
  return resposta.data;
}

/** `POST /api/empresas/{empresaId}/contas-correntes` (201). */
export async function criarConta(
  empresaId: string,
  corpo: ContaCorrenteIn,
): Promise<ContaCorrenteOut> {
  const resposta = await cliente.post<ContaCorrenteOut>(
    `/empresas/${empresaId}/contas-correntes`,
    corpo,
  );
  return resposta.data;
}

/** `PATCH /api/empresas/{empresaId}/contas-correntes/{contaId}`. */
export async function atualizarConta(
  empresaId: string,
  contaId: string,
  corpo: ContaCorrentePatch,
): Promise<ContaCorrenteOut> {
  const resposta = await cliente.patch<ContaCorrenteOut>(
    `/empresas/${empresaId}/contas-correntes/${contaId}`,
    corpo,
  );
  return resposta.data;
}

/* ───────────────────────── Usuarios ───────────────────────── */

/** `GET /api/usuarios`. */
export async function buscarUsuarios(): Promise<UsuarioOut[]> {
  const resposta = await cliente.get<UsuarioOut[]>("/usuarios");
  return resposta.data;
}

/** `GET /api/usuarios/{id}`. */
export async function buscarUsuario(id: string): Promise<UsuarioOut> {
  const resposta = await cliente.get<UsuarioOut>(`/usuarios/${id}`);
  return resposta.data;
}

/** `POST /api/usuarios` (201). `corpo.senha` e write-only (nunca volta). */
export async function criarUsuario(corpo: UsuarioIn): Promise<UsuarioOut> {
  const resposta = await cliente.post<UsuarioOut>("/usuarios", corpo);
  return resposta.data;
}

/** `PATCH /api/usuarios/{id}`. `corpo.senha` opcional e write-only. */
export async function atualizarUsuario(
  id: string,
  corpo: UsuarioPatch,
): Promise<UsuarioOut> {
  const resposta = await cliente.patch<UsuarioOut>(`/usuarios/${id}`, corpo);
  return resposta.data;
}

/* ───────────────────────── Perfis ───────────────────────── */

/** `GET /api/perfis` — catalogo read-only. */
export async function buscarPerfis(): Promise<PerfilOut[]> {
  const resposta = await cliente.get<PerfilOut[]>("/perfis");
  return resposta.data;
}
